import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from "@angular/core";
import {ContactPersonFormService} from "./contact-person.form.service";
import {StandaloneFormComponent} from "../../../umbrella-common/components/standalone-form.component";
import {ContactPerson, Country, Order} from "../../../store/state/order.state";
import {FormGroup} from "@angular/forms";
import {AuthenticationService} from "../../../authentication/authentication.service";
import {AccountExistsValidatorFactory} from "../../../umbrella-common/validation/account-exists-validator.service";
import {takeWhile} from "rxjs/operators";
import {ContactPersonBillingFormService} from "./contact-person-billing.form.service";
import {Actions, ofType} from "@ngrx/effects";
import {LoginActions} from "../../../store/actions/login.actions";
import {LocaleService} from "../../../umbrella-common/services/locale.service";

@Component({
    selector: 'contact-person',
    templateUrl: './contact-person.template.html',
    styleUrls: ['./contact-person.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ContactPersonComponent extends StandaloneFormComponent implements OnInit, OnChanges, OnDestroy {

	private readonly AR_EMAIL_HINT = 'You can\'t change the email address as it\'s client account login too.';
	private readonly CLIENT_EMAIL_HINT = 'order-edit.email-change-notice';

	@Input() order: Order;
	@Input() emailBlocked: boolean;
	@Input() showEmailHint: boolean;
	@Input() showSalespeople: boolean = true;
	@Input() salespeople: string[];
	@Input() countries: Country[];
	@Input() includeAccountExistsValidation: boolean = false;
	@Input() isBillingContactPerson = false;

	@Output() isFormValidEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

	showEmailReadOnlyText: boolean = false;
	form: FormGroup;
	formService: ContactPersonFormService | ContactPersonBillingFormService;
	private alive: boolean;

	constructor(private personFormService: ContactPersonFormService,
				private billingPersonFormService: ContactPersonBillingFormService,
				private authService: AuthenticationService,
				private changeDetector: ChangeDetectorRef, private actions$: Actions,
				private localeService: LocaleService) {
		super(null);
	}

	ngOnInit(): void {
		this.alive = true;
		const emailToExcludeFromAccountValidation = this.includeAccountExistsValidation ? this.order.contactPerson.email : null;
		if (this.isBillingContactPerson) {
			this.formService = this.billingPersonFormService;
			this.form = this.billingPersonFormService.buildStandaloneForm(this.order);
		} else {
			this.formService = this.personFormService;
			this.form = this.personFormService.buildStandaloneForm(this.order, this.includeAccountExistsValidation, emailToExcludeFromAccountValidation, this.countries);
		}

		this.setStandaloneFormService(this.formService);
		if (this.emailBlocked) {
			this.getFormControl('email').disable();
			this.showEmailReadOnlyText = true;
		}
		this.actions$.pipe(ofType(LoginActions.LOG_OUT)).subscribe(() => {
			if (this.formService.isFormInitialized()) {
				this.getFormControl('email').enable();
				this.getFormControl('email').setValue('');
				this.showEmailReadOnlyText = false;
			}
		});

		AccountExistsValidatorFactory.validationFinished$().pipe(takeWhile(() => this.alive))
			.subscribe(() => {
				this.changeDetector.detectChanges();
				this.changeDetector.markForCheck();
			});

		this.formService.isFormValid().subscribe(isValid => this.isFormValidEvent.emit(isValid))
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.formService && this.formService.isFormInitialized()) {
			if (this.emailBlocked && this.getFormControl('email') && this.authService.isLoggedUserApplicationReviewer()) {
				this.getFormControl('email').disable();
				this.showEmailReadOnlyText = true;
			}
		}
	}

	override ngOnDestroy() {
		this.alive = false;
		super.ngOnDestroy();
	}

	emailHint(): string {
		return this.authService.isLoggedUserApplicationReviewer() ? this.AR_EMAIL_HINT : this.CLIENT_EMAIL_HINT;
	}

	get countryCodes() {
		const callPrefixes = this.countries.map(c => c.callPrefix);
		return callPrefixes.filter((n, i) => callPrefixes.indexOf(n) === i);
	}

	getContactPerson(): ContactPerson {
		return this.billingPersonFormService.getContactPerson();
	}

	getPlaceholderKey(): string {
		return 'contact-person.phone-number-example.' + this.localeService.currentLocale().split('-')[1];
	}

}
